// «Nuovo progetto» (spec «Struttura della plancia», S3; tela, artboard 31–34): origine,
// requisiti (triage con PM e Team leader o «Ho già tutto»), team e piano, riepilogo. Il triage e
// la proposta lavorano su una bozza del progetto, creata al primo bisogno e tolta da «Annulla».
// «Salva» crea l'attività progettuale non pianificata in Pianifica; «Salva e avvia» la fa
// partire subito. Se memo è acceso, i requisiti vanno anche lì.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Banner } from '../../../components/Banner';
import { Button } from '../../../components/Button';
import { SkeletonRow } from '../../../components/Skeleton';
import { ApiError, api } from '../../../lib/api';
import type {
  AgentProfile,
  ConnectedProvider,
  GitRegistry,
  Pipeline,
  PlanMember,
  PlanMemo,
  ProjectInput,
  ProjectPlan,
  TeamProposal,
} from '../../../lib/api';
import { useStore } from '../../../store/StoreProvider';
import { PhasesEditor } from './PhasesEditor';
import { EMPTY_PLAN, loadPopNames, memberOf, proposePhases, proposeTeam } from './plan';
import { StepOrigin, type Origin } from './StepOrigin';
import { StepRequirements, type ReqMode } from './StepRequirements';
import { StepSummary } from './StepSummary';
import { STEPS, Stepper } from './Stepper';
import { TeamEditor } from './TeamEditor';
import { EMPTY_TRIAGE, type TriageState } from './Triage';

interface Loaded {
  defaults: Omit<ProjectInput, 'name' | 'description'>;
  registries: GitRegistry[];
  profiles: AgentProfile[];
  providers: ConnectedProvider[];
  triageRoles: { lead: string; technical: string };
  pipelines: Pipeline[];
  memoLinks: { id: string; name: string }[];
}

const reason = (e: unknown, fallback: string) =>
  e instanceof ApiError && e.detail ? e.detail : fallback;

export function NewProject() {
  const nav = useNavigate();
  const { toast, reload } = useStore();
  const [data, setData] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [origin, setOrigin] = useState<Origin>('new');
  const [reqMode, setReqMode] = useState<ReqMode>('triage');
  const [form, setForm] = useState<ProjectInput | null>(null);
  const [plan, setPlan] = useState<ProjectPlan>(EMPTY_PLAN);
  const [busy, setBusy] = useState<'save' | 'start' | 'propose' | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [triage, setTriage] = useState<TriageState>(EMPTY_TRIAGE);
  const [docs, setDocs] = useState('');

  useEffect(() => {
    Promise.all([
      api.projects(),
      api.gitRegistries(),
      api.profiles(),
      api.providers(),
      api.pipelines(),
      api.memoLinks().catch(() => []),
      loadPopNames(),
    ]).then(
      ([p, g, profiles, prov, pipelines, memoLinks]) => {
        setData({
          defaults: p.defaults,
          registries: g.registries,
          profiles,
          providers: prov.providers,
          triageRoles: p.triage ?? { lead: 'pm', technical: 'team_leader' },
          pipelines,
          memoLinks,
        });
        setForm({ ...p.defaults, name: '', description: '' });
        // le proposte di partenza: un pop per ruolo di lavoro, quattro fasi e l'inizio oggi,
        // tutte da cambiare
        const team = proposeTeam(profiles);
        const today = new Date().toLocaleDateString('sv-SE'); // AAAA-MM-GG nel fuso del browser
        setPlan({ ...EMPTY_PLAN, start: today, team, phases: proposePhases(team, profiles) });
      },
      (e: unknown) => setError(reason(e, 'Non riesco a leggere progetti, git e Pops.')),
    );
  }, []);

  const set = (patch: Partial<ProjectInput>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const setPlanPart = (patch: Partial<ProjectPlan>) => setPlan((p) => ({ ...p, ...patch }));
  const memo: PlanMemo = plan.memo ?? {
    enabled: false,
    connection: null,
    space: '',
    project: form?.name ?? '',
    subject: null,
  };

  // la bozza del progetto, per il triage e la proposta: si crea al primo bisogno e si aggiorna
  const ensureProject = async (): Promise<string> => {
    if (!form) throw new Error('form vuoto');
    const body = { ...form, plan };
    if (draft) {
      await api.saveProject(draft, body);
      return draft;
    }
    const p = await api.addProject(body);
    setDraft(p.id);
    return p.id;
  };

  const runTriage = async (reply: string | null) => {
    const id = await ensureProject();
    return api.projectTriage(id, {
      sessionId: triage.sessionId,
      reply,
      requirements: plan.requirements,
      docs,
      models: triage.models,
    });
  };

  const applyProposal = (prop: TeamProposal) => {
    if (!data) return;
    const team: PlanMember[] = [];
    for (const t of prop.team) {
      const profile = data.profiles.find((x) => x.id === t.profile);
      for (let n = 0; profile && n < t.count; n++)
        team.push({ ...memberOf(profile, team), phase: t.phase });
    }
    const phases = prop.phases.map((ph) => ({
      name: ph.name,
      description: ph.description,
      owner: team.find((m) => m.profile === ph.owner)?.name ?? '',
      model: null,
      days: ph.days,
    }));
    setPlan((p) => ({
      ...p,
      team: team.length ? team : p.team,
      phases: phases.length ? phases : p.phases,
    }));
  };

  const propose = async () => {
    setBusy('propose');
    try {
      const id = await ensureProject();
      const prop = await api.projectProposal(id, plan.requirements, triage.models.technical.model);
      if (prop.ok) {
        applyProposal(prop);
        toast({ kind: 'success', title: 'Proposta pronta', body: 'Cambia quello che vuoi.' });
      } else toast({ kind: 'danger', title: 'Nessuna proposta', body: prop.error ?? 'Riprova.' });
    } catch (e) {
      toast({ kind: 'danger', title: 'Nessuna proposta', body: reason(e, 'Riprova fra poco.') });
    } finally {
      setBusy(null);
    }
  };

  // un progetto importato parte dal README del suo repository, se le docs sono ancora vuote
  useEffect(() => {
    if (step !== 1 || docs || !form?.git?.repo) return;
    void api.gitReadme(form.git.registry, form.git.repo).then(
      (r) => r.text && setDocs(`README.md di ${form.git?.repo}:\n\n${r.text}`),
      () => undefined,
    );
  }, [step, docs, form?.git]);

  const goTo = (next: number) => {
    // lasciando i requisiti il triage è finito: la sua sessione si chiude
    if (step === 1 && next > 1 && draft && triage.sessionId && !triage.done) {
      void api.projectTriageClose(draft, triage.sessionId).catch(() => undefined);
      setTriage((t) => ({ ...t, done: true }));
    }
    setStep(next);
  };

  const cancel = async () => {
    if (draft) await api.removeProject(draft).catch(() => undefined);
    nav('/progetti');
  };

  // cosa manca per andare avanti dal passo aperto
  const blocker = useMemo(() => {
    if (!form) return 'Carico…';
    if (step === 0) {
      if (!form.name.trim()) return 'Dai un nome al progetto.';
      if (origin === 'import' && !form.git?.repo) return 'Scegli il repository da importare.';
    }
    if (step === 1 && plan.memo?.enabled && !(plan.memo.space.trim() && plan.memo.project.trim()))
      return 'Per memo servono space e progetto.';
    if (step === 2) {
      const names = plan.team.map((m) => m.name.trim());
      if (names.some((n) => !n)) return 'Ogni pop del team ha un nome.';
      if (new Set(names).size !== names.length) return 'Due pops hanno lo stesso nome.';
      if (plan.phases.some((p) => !p.name.trim())) return 'Ogni fase ha un nome.';
    }
    return null;
  }, [form, step, origin, plan]);

  const finish = async (start: boolean) => {
    if (!form || !data) return;
    setBusy(start ? 'start' : 'save');
    try {
      const id = await ensureProject();
      if (triage.sessionId && !triage.done)
        await api.projectTriageClose(id, triage.sessionId).catch(() => undefined);
      if (memo.enabled) {
        const m = await api.projectMemo(id);
        if (!m.ok) toast({ kind: 'danger', title: 'memo non aggiornato', body: m.error ?? '' });
      }
      // l'attività progettuale: in Pianifica, a mano, con requisiti, team e fasi come compito
      const { text } = await api.projectBrief(id);
      const routine = await api.createRoutine({
        name: form.name,
        task: text,
        pipeline: plan.pipeline ?? data.pipelines[0]?.id ?? 'generic',
        trigger: 'manual',
        project: id,
      });
      if (!start) {
        toast({
          kind: 'success',
          title: `${form.name}: salvato`,
          body: 'In Pianifica, da avviare.',
        });
        nav('/progetti/' + id);
        return;
      }
      const s = await api.runRoutine(routine.id);
      toast({ kind: 'success', title: `${form.name}: avviato`, body: 'Il PM prepara i lavori.' });
      reload();
      nav('/s/' + s.id);
    } catch (e) {
      toast({
        kind: 'danger',
        title: 'Non riesco a salvare',
        body: reason(e, 'Riprova fra poco.'),
      });
      setBusy(null);
    }
  };

  const phaseNames = plan.phases.map((p) => p.name).filter(Boolean);

  return (
    <div className="page np-page">
      <div className="t-caption dim">
        <Link to="/progetti" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
          Progetti
        </Link>{' '}
        › Nuovo
      </div>
      <h1 className="t-h1" style={{ margin: '4px 0 12px' }}>
        Nuovo progetto
      </h1>
      <Stepper at={step} onGo={goTo} />
      {error && <Banner kind="danger">{error}</Banner>}
      {!data || !form ? (
        !error && <SkeletonRow />
      ) : (
        <div className="np-body">
          {step === 0 && (
            <StepOrigin
              origin={origin}
              onOrigin={setOrigin}
              form={form}
              set={set}
              registries={data.registries}
            />
          )}
          {step === 1 && (
            <StepRequirements
              mode={reqMode}
              onMode={setReqMode}
              value={plan.requirements}
              onChange={(requirements) => setPlanPart({ requirements })}
              triage={triage}
              onTriage={setTriage}
              docs={docs}
              onDocs={setDocs}
              profiles={data.profiles}
              providers={data.providers}
              triageRoles={data.triageRoles}
              runTriage={runTriage}
              memo={memo}
              onMemo={(m) => setPlanPart({ memo: { ...m, project: m.project || form.name } })}
              memoLinks={data.memoLinks}
            />
          )}
          {step === 2 && (
            <>
              <div className="np-propose">
                <span className="t-small dim">
                  Il Team leader può proporre team e fasi dai requisiti; poi cambi quello che vuoi.
                </span>
                <Button
                  icon="sparkles"
                  loading={busy === 'propose'}
                  disabled={!plan.requirements.trim() || !!busy}
                  onClick={() => void propose()}
                >
                  Proponi team e fasi
                </Button>
              </div>
              <TeamEditor
                team={plan.team}
                onChange={(team) => setPlanPart({ team })}
                profiles={data.profiles}
                providers={data.providers}
                phases={phaseNames}
              />
              <PhasesEditor
                plan={plan}
                onChange={setPlanPart}
                team={plan.team}
                providers={data.providers}
                pipelines={data.pipelines}
              />
            </>
          )}
          {step === 3 && (
            <StepSummary
              form={form}
              plan={plan}
              registries={data.registries}
              profiles={data.profiles}
              onEdit={goTo}
            />
          )}
        </div>
      )}
      <div className="np-actions">
        {step === 0 ? (
          <Button onClick={() => void cancel()}>Annulla</Button>
        ) : (
          <Button icon="arrow-left" onClick={() => goTo(step - 1)}>
            Indietro
          </Button>
        )}
        <span className="t-caption dim np-actions-note">
          {blocker && step < 3
            ? blocker
            : step === 3
              ? '«Salva» mette il progetto in Pianifica, da avviare; «Salva e avvia» lo fa partire subito.'
              : ''}
        </span>
        {step < 3 ? (
          <Button variant="primary" disabled={!!blocker} onClick={() => goTo(step + 1)}>
            Avanti: {STEPS[step + 1].toLowerCase()}
          </Button>
        ) : (
          <>
            <Button loading={busy === 'save'} disabled={!!busy} onClick={() => void finish(false)}>
              Salva
            </Button>
            <Button
              variant="primary"
              icon="play"
              loading={busy === 'start'}
              disabled={!!busy}
              onClick={() => void finish(true)}
            >
              Salva e avvia
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
